import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Plus, Trash2 } from 'lucide-react';

const FleetList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API_URL}/provider/fleet`);
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch fleet listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name || 'this fleet listing'}"?`)) return;
    try {
      await axios.delete(`${API_URL}/provider/fleet/${id}`);
      toast.success('Fleet deleted successfully');
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete fleet');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-pulse text-foreground font-heading text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary">
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl font-black text-foreground tracking-tight mb-2" data-testid="fleet-list-title">
              My Fleet
            </h1>
            <p className="text-muted-foreground">Manage your vehicle and transport listings</p>
          </div>
          <Button
            onClick={() => navigate('/provider/add-fleet')}
            data-testid="add-fleet-button"
            className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm transition-all active:scale-95"
          >
            <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />
            Add Fleet
          </Button>
        </div>

        {items.length === 0 ? (
          <Card className="bg-white border border-border rounded-sm shadow-sm p-12 text-center" data-testid="fleet-empty-state">
            <h3 className="font-heading text-xl font-bold text-foreground mb-2">No fleet listings yet</h3>
            <p className="text-muted-foreground mb-6">Start by adding your first vehicle or transport service</p>
            <Button onClick={() => navigate('/provider/add-fleet')} className="rounded-sm">
              Add Fleet
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="fleet-grid">
            {items.map((item) => (
              <Card
                key={item.id}
                data-testid={`fleet-${item.id}`}
                className="bg-white border border-border rounded-sm shadow-sm p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-heading text-xl font-bold text-foreground">{item.name || item.fleet_type}</h3>
                </div>

                <div className="flex items-center gap-1 text-sm text-muted-foreground mb-4">
                  <MapPin className="h-4 w-4" strokeWidth={1.5} />
                  <span>{item.location_city}{item.location_state ? `, ${item.location_state}` : ''}, {item.location_country}</span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Type:</span>
                    <span className="font-medium text-foreground capitalize">{item.fleet_type.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Quantity:</span>
                    <span className="font-medium text-foreground">{item.quantity}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Condition:</span>
                    <span className="font-medium text-foreground capitalize">{item.condition}</span>
                  </div>
                  {item.license_plate && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">License:</span>
                      <span className="font-medium text-foreground">{item.license_plate}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Rate:</span>
                    <span className="font-medium text-foreground">{item.currency} {item.rate}/{item.pricing_mode}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap mb-4">
                  <span className="px-2.5 py-0.5 bg-slate-100 text-slate-600 border border-slate-200 rounded-full text-xs font-semibold uppercase tracking-wide">
                    {item.pricing_mode}
                  </span>
                  <span className="px-2.5 py-0.5 bg-slate-100 text-slate-600 border border-slate-200 rounded-full text-xs font-semibold uppercase tracking-wide">
                    {item.condition}
                  </span>
                </div>

                <div className="pt-4 border-t border-border flex items-center justify-between">
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide ${
                    item.available ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-red-100 text-red-700 border border-red-200'
                  }`}>
                    {item.available ? 'Available' : 'Unavailable'}
                  </span>
                  <Button
                    onClick={() => handleDelete(item.id, item.name || item.fleet_type)}
                    variant="ghost"
                    size="sm"
                    data-testid={`delete-fleet-${item.id}`}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10 rounded-sm"
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FleetList;
